// Decompression, using the platform's own inflater.
//
// Reading a .docx or a PDF means inflating deflate streams, which used to mean
// shipping a compression library. Every browser this app targets — and Node 18
// and up, which is what the tests run on — has DecompressionStream built in,
// so the whole job is feeding it bytes.
//
// Written against the streams directly rather than the shorter
// `new Blob([bytes]).stream()` / `new Response(stream).arrayBuffer()` spelling,
// because that pulls in two more platform objects for no benefit and neither
// is guaranteed present in a test environment.

/**
 * Inflate `bytes`.
 *
 * @param {Uint8Array} bytes
 * @param {'deflate'|'deflate-raw'|'gzip'} format zlib-wrapped, raw, or gzip
 * @returns {Promise<Uint8Array>}
 */
export const inflate = async (bytes, format) => {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot decompress the file (DecompressionStream unavailable).');
  }

  const stream = new DecompressionStream(format);
  const writer = stream.writable.getWriter();
  // Write and read at the same time: a stream large enough to fill the
  // internal queue deadlocks if the whole input is pushed before reading.
  const written = (async () => {
    await writer.write(bytes);
    await writer.close();
  })();
  // Bad input rejects both ends of the stream. The reader's rejection is the
  // one callers see; without this the writer's identical copy escapes as an
  // unhandled rejection and takes the process down with it.
  written.catch(() => {});

  const reader = stream.readable.getReader();
  const chunks = [];
  let length = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    length += value.length;
  }
  await written.catch(() => {});

  const result = new Uint8Array(length);
  let offset = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, offset);
    offset += chunk.length;
  });
  return result;
};

/**
 * Inflate a PDF FlateDecode stream. It is meant to be zlib-wrapped, but some
 * writers emit raw deflate, so both are tried before giving up.
 */
export const inflateFlate = async (bytes) => {
  try {
    return await inflate(bytes, 'deflate');
  } catch (error) {
    return inflate(bytes, 'deflate-raw');
  }
};

export default inflate;
